// Ara IndexedDB wrapper — fully isolated from AravelaDB (Vela).
// Mirrors the proven pattern from storage.js but writes to AraSanctuaryDB,
// its own stores, and its own encryption key. Vela and Ara cannot see each
// other's data even on the same device.
import { encryptData, decryptData } from './crypto';

const DB_NAME = 'AraSanctuaryDB';
const DB_VERSION = 1;

export const ARA_STORES = {
  AUTH: 'auth',                 // salt (unencrypted, standard)
  SESSIONS: 'sessions',         // encrypted saved Ara conversations
  REFLECTIONS: 'reflections',   // encrypted saved reflections
  APP_STATE: 'app_state',       // preferences, onboarding flags, theme
};

let dbInstance = null;

export const initAraDB = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => {
      dbInstance = request.result;
      resolve(dbInstance);
    };
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(ARA_STORES.AUTH)) {
        db.createObjectStore(ARA_STORES.AUTH, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(ARA_STORES.SESSIONS)) {
        const s = db.createObjectStore(ARA_STORES.SESSIONS, { keyPath: 'id' });
        s.createIndex('startTime', 'startTime', { unique: false });
      }
      if (!db.objectStoreNames.contains(ARA_STORES.REFLECTIONS)) {
        const r = db.createObjectStore(ARA_STORES.REFLECTIONS, { keyPath: 'id' });
        r.createIndex('createdAt', 'createdAt', { unique: false });
      }
      if (!db.objectStoreNames.contains(ARA_STORES.APP_STATE)) {
        db.createObjectStore(ARA_STORES.APP_STATE, { keyPath: 'key' });
      }
    };
  });
};

const getDB = async () => dbInstance || (await initAraDB());

export const saveAraEncrypted = async (storeName, data, key) => {
  const db = await getDB();
  const encrypted = await encryptData(data, key);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    // Store ONLY the encrypted blob plus id for lookup. No plaintext.
    const req = store.put({ ...encrypted, id: data.id });
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export const getAllAraEncrypted = async (storeName, key) => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readonly');
    const store = tx.objectStore(storeName);
    const req = store.getAll();
    req.onsuccess = async () => {
      const results = [];
      for (const record of req.result) {
        try {
          const decrypted = await decryptData(record, key);
          results.push({ ...decrypted, id: record.id });
        } catch (e) {
          // Skip unreadable record — do NOT delete; preserve data.
        }
      }
      resolve(results);
    };
    req.onerror = () => reject(req.error);
  });
};

export const deleteAraData = async (storeName, id) => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite');
    const store = tx.objectStore(storeName);
    const req = store.delete(id);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

export const getAraAuth = async () => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ARA_STORES.AUTH, 'readonly');
    const store = tx.objectStore(ARA_STORES.AUTH);
    const req = store.get('passcode');
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
};

export const setAraAuth = async (saltRecord) => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ARA_STORES.AUTH, 'readwrite');
    const store = tx.objectStore(ARA_STORES.AUTH);
    const req = store.put({ id: 'passcode', ...saltRecord });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};

export const getAraAppState = async (key) => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ARA_STORES.APP_STATE, 'readonly');
    const store = tx.objectStore(ARA_STORES.APP_STATE);
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result?.value);
    req.onerror = () => reject(req.error);
  });
};

export const setAraAppState = async (key, value) => {
  const db = await getDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ARA_STORES.APP_STATE, 'readwrite');
    const store = tx.objectStore(ARA_STORES.APP_STATE);
    const req = store.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error);
  });
};
